import { DateRangePicker } from '@crazyegginc/hatch';

import { DateRange } from '/src/components/date-time/DateRange';
import { TIMEZONES } from '/src/features/_global/constants';

const GOOGLE = 'GOOGLE';
const META = 'META';
const REDDIT = 'REDDIT';

export function PreviewTopBar({
  selectedSite,
  connections = [],
  loading = false,
  dateRangeConfig,
  resolvedDateRange,
  chooseDateRange,
  onGoogleConnect,
  onMetaConnect,
  onRedditConnect,
  metaConnectLoading = false,
  redditConnectLoading = false,
}) {
  const googleConnected = connections.some((connection) => connection.platform === GOOGLE);
  const metaConnected = connections.some((connection) => connection.platform === META);
  const redditConnected = connections.some((connection) => connection.platform === REDDIT);
  const connectedCount = [googleConnected, metaConnected, redditConnected].filter(Boolean).length;

  const selectedSiteName = selectedSite?.name || 'this site';

  return (
    <section className="rounded-[28px] border border-mystic-500 bg-white p-6 shadow-sm xl:p-7">
      <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
        <div className="max-w-[620px]">
          <div className="inline-flex items-center gap-2 rounded-full border border-solitude-500 bg-solitude-500 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">
            <span
              className={`h-2 w-2 rounded-full ${connectedCount > 0 ? 'bg-lima-500' : 'bg-cadet-blue-500'}`}
              aria-hidden="true"
            />
            Ad Networks ({connectedCount}/3)
          </div>
          <h2 className="mt-4 text-[28px] font-semibold leading-tight text-black-pearl-500">
            Connect ad accounts for {selectedSiteName}
          </h2>
          <p className="mt-3 max-w-[58ch] text-sm leading-6 text-lynch-500">
            Connect Google, Meta, or Reddit accounts so the selected site can import synced ad creative and performance
            data.
          </p>
        </div>

        <div className="flex w-full flex-col gap-3 xl:max-w-[380px]">
          <NetworkButton
            label={googleConnected ? 'Google Ads Connected' : 'Connect Google Ads'}
            description={googleConnected ? 'This site already has an enabled Google Ads connection.' : 'Available now'}
            accent="google"
            disabled={loading || !selectedSite || googleConnected}
            onClick={onGoogleConnect}
            dataTestId="connect-google-ads"
          />
          <NetworkButton
            label={metaConnected ? 'Meta Ads Connected' : 'Connect Meta Ads'}
            description={metaConnected ? 'This site already has an enabled Meta connection.' : 'Available now'}
            accent="meta"
            disabled={loading || !selectedSite || metaConnected || metaConnectLoading}
            onClick={onMetaConnect}
            dataTestId="connect-meta-ads"
          />
          <NetworkButton
            label={redditConnected ? 'Reddit Ads Connected' : 'Connect Reddit Ads'}
            description={redditConnected ? 'This site already has an enabled Reddit connection.' : 'Available now'}
            accent="reddit"
            disabled={loading || !selectedSite || redditConnected || redditConnectLoading}
            onClick={onRedditConnect}
            dataTestId="connect-reddit-ads"
          />
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-solitude-500 bg-[#FBFCFE] p-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Preview range</p>
          <div className="mt-2 text-lg font-semibold text-black-pearl-500" data-testid="preview-date-range">
            <DateRange dateRange={dateRangeConfig} timeZone={TIMEZONES.UTC} />
          </div>
          <p className="mt-2 text-sm text-lynch-500">
            The date picker is already live here so the top bar matches the eventual ad-performance experience.
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 lg:w-auto lg:items-end">
          <DateRangePicker
            lowerBoundary={resolvedDateRange?.pickerProps.lowerBoundary}
            upperBoundary={resolvedDateRange?.pickerProps.upperBoundary}
            startDate={resolvedDateRange?.pickerProps.startDate}
            endDate={resolvedDateRange?.pickerProps.endDate}
            special={resolvedDateRange?.pickerProps.special}
            setDateRange={chooseDateRange}
            setCommonDateRange={chooseDateRange}
            showCommonRanges={true}
            size="lg"
            maxRange={resolvedDateRange?.pickerProps.maxRange}
            timezone={TIMEZONES.UTC}
            showTimeZone={true}
          />
          {loading ? (
            <p className="text-xs font-medium text-cadet-blue-500">
              Loading connection status for the selected site...
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function NetworkButton({ label, description, accent, disabled, onClick, dataTestId }) {
  const accentClassByPlatform = {
    google: 'border-[#D7E7FF] bg-[#F5FAFF] text-[#00509D]',
    meta: 'border-[#F1E0FF] bg-[#FBF7FF] text-[#5B3E8C]',
    reddit: 'border-[#FFD6C7] bg-[#FFF7F4] text-[#B64217]',
  };

  const enabledClassByPlatform = {
    google: 'hover:border-[#BFD7FF] hover:bg-[#ECF5FF] focus-visible:outline-[#00509D]',
    meta: 'hover:border-[#E5D0FF] hover:bg-[#F8F1FF] focus-visible:outline-[#5B3E8C]',
    reddit: 'hover:border-[#FFC4AD] hover:bg-[#FFF0EA] focus-visible:outline-[#B64217]',
  };

  const accentClasses = accentClassByPlatform[accent];
  const enabledClasses = enabledClassByPlatform[accent];

  return (
    <button
      type="button"
      className={[
        'flex w-full items-start justify-between gap-4 rounded-2xl border px-4 py-3 text-left transition',
        accentClasses,
        disabled ? 'cursor-not-allowed opacity-70' : enabledClasses,
      ].join(' ')}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      data-testid={dataTestId}
    >
      <div>
        <div className="text-sm font-semibold">{label}</div>
        <div className="mt-1 text-xs font-medium text-lynch-500">{description}</div>
      </div>
      <span
        className={`mt-1 inline-flex h-2.5 w-2.5 flex-shrink-0 rounded-full ${disabled ? 'bg-cadet-blue-500/50' : 'bg-lima-500'}`}
        aria-hidden="true"
      />
    </button>
  );
}
